(() => {
  class ProductVariantPicker extends HTMLElement {
    connectedCallback() {
      this.variantData = this.getVariantData();
      this.optionButtons = Array.from(this.querySelectorAll('[data-option-index]'));
      this.selectedOptions = this.getSelectedOptions();
      this.onOptionClick = this.onOptionClick || ((event) => {
        // Keep native navigation for new-tab clicks and when variant data is unavailable.
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (this.onOptionSelect(event.currentTarget)) event.preventDefault();
      });

      this.optionButtons.forEach((button) => {
        button.addEventListener('click', this.onOptionClick);
      });

      this.updateFromVariant(this.getSelectedVariant(), { updateUrl: false });
    }

    disconnectedCallback() {
      this.optionButtons.forEach((button) => button.removeEventListener('click', this.onOptionClick));
    }

    getVariantData() {
      const script = this.querySelector('[data-product-variants]');

      if (!script) return [];

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return [];
      }
    }

    getSelectedOptions() {
      const selected = [];

      this.optionButtons.forEach((button) => {
        if (button.classList.contains('is-selected')) {
          selected[Number(button.dataset.optionIndex)] = button.dataset.optionValue;
        }
      });

      return selected;
    }

    onOptionSelect(button) {
      const optionIndex = Number(button.dataset.optionIndex);
      const fallbackVariant = this.findCandidateVariant(optionIndex, button.dataset.optionValue);

      if (!fallbackVariant) return false;

      this.selectedOptions = [...fallbackVariant.options];
      this.updateFromVariant(fallbackVariant, { updateUrl: true });
      return true;
    }

    getSelectedVariant() {
      const variantFromUrl = this.getVariantFromUrl();

      if (variantFromUrl) return variantFromUrl;

      return this.findVariantByOptions(this.selectedOptions) || this.variantData[0];
    }

    getVariantFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const variantId = Number(params.get('variant'));

      if (!variantId) return null;

      return this.variantData.find((variant) => variant.id === variantId);
    }

    findVariantByOptions(options) {
      return this.variantData.find((variant) => {
        return variant.options.every((option, index) => option === options[index]);
      });
    }

    findVariantByOption(optionIndex, value) {
      return this.variantData.find((variant) => variant.options[optionIndex] === value);
    }

    findCandidateVariant(optionIndex, value) {
      const options = [...this.selectedOptions];
      options[optionIndex] = value;

      return this.findVariantByOptions(options) || this.findVariantByOption(optionIndex, value);
    }

    updateFromVariant(variant, { updateUrl }) {
      if (!variant) return;

      this.selectedOptions = [...variant.options];
      this.updateOptionButtons();
      this.dispatchVariantChange(variant);

      if (updateUrl) {
        this.updateUrl(variant);
      }
    }

    updateOptionButtons() {
      this.optionButtons.forEach((button) => {
        const optionIndex = Number(button.dataset.optionIndex);
        const candidateVariant = this.findCandidateVariant(optionIndex, button.dataset.optionValue);
        const isSelected = this.selectedOptions[optionIndex] === button.dataset.optionValue;
        const priceElement = button.querySelector('[data-variant-option-price]');

        button.classList.toggle('is-selected', isSelected);
        button.classList.toggle('is-unavailable', !candidateVariant || !candidateVariant.available);
        button.setAttribute('aria-current', String(isSelected));

        if (candidateVariant) {
          const url = new URL(this.dataset.productUrl, window.location.href);
          url.searchParams.set('variant', candidateVariant.id);
          button.href = url.toString();
        }

        if (priceElement) {
          priceElement.textContent = isSelected || !candidateVariant ? '' : candidateVariant.price_text;
        }
      });
    }

    updateUrl(variant) {
      const url = new URL(window.location.href);

      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    dispatchVariantChange(variant) {
      document.dispatchEvent(
        new CustomEvent('product:variant-change', {
          detail: { variant, scope: this.scope }
        })
      );
    }

    get scope() {
      return this.dataset.productUiScope || this.closest('.product-layout, .featured-product-showcase, [data-search-product-drawer]') || '';
    }
  }

  if (!customElements.get('variant-picker')) {
    customElements.define('variant-picker', ProductVariantPicker);
  }
})();
